<template>
  <section>
    <PageTitle>
      <span v-html="$store.getters['app/getMenuName']('simulation')" />
    </PageTitle>

    <PageContent v-if="this.simulationBridalItems.length === 0">
      <Container>
        <NoticeBox
          type="error"
          :borderless="true"
          class="space-b-2 text-bold"
        >
          現在、見積シミュレーションがありません
        </NoticeBox>
        <p class="text-paragraph text-xs">
          現在表示される見積シミュレーションがありません。
          <br class="mobile-only" />
          見積シミュレーションがある場合は、この画面に表示されます。
        </p>
        <div class="form-footer">
          <div class="text-center space-t-3" v-if="history">
            <a href="#" @click.prevent="$router.back()">
              前の画面へ戻る
            </a>
          </div>
        </div>
      </Container>
    </PageContent>
    <PageContent v-else>
      <form @submit.prevent="onSubmit">
        <Container>
          <NoticeBox
            v-if="isUsageLimitOver"
            type="info"
            :icon-class="['fas', 'fa-lock']"
          >
            利用制限日を過ぎたため、変更できません
          </NoticeBox>
          <div
            class="simulation"
            :class="isPlanner ? 'is-planner' : 'is-customer'"
          >
            <div class="simulation-header">
              <div class="simulation-amount">
                <span class="total-label">Total</span>
                <span class="total-amount">
                  &yen;{{totalAmount | priceFormat}}
                </span>
              </div>
              <div class="simulation-checkall" v-if="isPlanner && editable">
                <label for="item-select-all" class="checkbox">
                  <input
                    id="item-select-all"
                    type="checkbox"
                    v-model="all"
                    @change="onAllCheckToggle"
                  />
                  <span id="checkall" class="checkbox-text">確定</span>
                </label>
              </div>
            </div>
            <div class="simulation-table">
              <div
                class="simulation-group"
                v-for="groupRow in simulationBridalItemList"
                v-bind:key="groupRow.groupId"
              >
                <div class="bridal-item-group">
                  <Headline :level="2">
                    {{$domUtils.decode(groupRow.groupName)}}
                  </Headline>
                  <div class="bridal-item-group-amount">
                    &yen;{{groupTotalAmount(groupRow.groupId) | priceFormat}}
                  </div>
                </div>
                <div
                  v-for="subgroupRow in groupRow.subgroupList"
                  v-bind:key="subgroupRow.subgroupId"
                  class="bridal-item-subgroup"
                >
                  <Headline :level="3">
                    {{$domUtils.decode(subgroupRow.subgroupName)}}
                  </Headline>
                  <div
                    v-for="row in subgroupRow.itemList"
                    :key="row.bridal_item_id"
                    class="bridal-item"
                  >
                    <SimulationBridalItem
                      :simulationBridalItemId="row.simulation_bridal_item_id"
                      :bridalItemGroupId="row.bridal_item_group_id"
                      :bridalItemGroupName="row.bridal_item_group_name"
                      :bridalItemGroupExclusionFlag="row.bridal_item_group_exclusion_flag"
                      :bridalItemSubgroupId="row.bridal_item_subgroup_id"
                      :bridalItemSubgroupName="row.bridal_item_subgroup_name"
                      :bridalItemName="row.bridal_item_name"
                      :bridalItemDescription="row.bridal_item_description"
                      :serviceAmountTarget="row.service_amount_target"
                      :taxExempt="row.tax_exempt"
                      :masterRetailPrice="row.master_retail_price.toLocaleString()"
                      :retailPriceEditableFlag="row.retail_price_editable_flag"
                      :quantityEditableFlag="row.quantity_editable_flag"
                      :itemNumber="row.item_number"
                      :alertMessage="row.alert_message"
                      :bridalItemId="row.bridal_item_id"
                      :imagePath="row.image_path"
                      :retailPrice="row.retail_price.toLocaleString()"
                      :quantity="row.quantity"
                      :fixFlag="row.fix_flag"
                      :retailAmount="row.retail_amount"
                      :revision="row.revision"
                      :deleted="row.deleted"
                      :editable="editable"
                      @changeRetailPrice="onChangeRetailPrice"
                      @changeQuantity="onChangeQuantity"
                      @delete="onDelete"
                      @undo="onUndo"
                      @check="onCheckToggle"
                      @showItemModal="onShowItemModal"
                    />
                  </div>
                </div>
              </div>
              <SimurationAmountDetail v-if="this.displayAmountDetail"
                :amount-detail=this.amountDetail
              >
              </SimurationAmountDetail>
            </div>
          </div>
          <div class="form-footer">
            <div class="text-center" v-if="editable">
              <WaveButton type="submit" :loading="loading">
                保存する
              </WaveButton>
            </div>
            <div class="text-center space-t-3" v-if="history">
              <a href="#" @click.prevent="$router.back()">
                前の画面へ戻る
              </a>
            </div>
          </div>
        </Container>
      </form>
    </PageContent>

    <Modal
      id="modal-complete"
      title="見積シミュレーションの保存が完了しました"
      icon="check"
      @ok="modalOk"
    />
    <SimpleModal
      id="bridal-item-modal"
      ref="bridalItemModal"
    >
      <template #default>
        <BridalItemCard
          class="in-modal"
          :item="item(itemModalTargetId)"
        ></BridalItemCard>
      </template>
    </SimpleModal>
  </section>
</template>

<script>
import Modal from '@/components/Modal.vue'
import SimulationBridalItem from '@/components/SimulationBridalItem.vue'
import simulationApi from '@/api/simulation'
import { renderError } from '@/utils/error'
import priceFormat from '@/filters/price-format'
import {getCommaRemovedPrice} from '@/utils/form'
import validationMixin from '@/mixins/validation'
import NoticeBox from '@/components/NoticeBox.vue'
import SimpleModal from '@/components/SimpleModal.vue'
import BridalItemCard from '@/components/BridalItemCard.vue'
import SimurationAmountDetail from '@/components/SimurationAmountDetail.vue'

export default {
  components: {
    SimulationBridalItem,
    Modal,
    NoticeBox,
    SimpleModal,
    BridalItemCard,
    SimurationAmountDetail,
  },

  mixins: [
    validationMixin
  ],

  metaInfo() {
    return {
      title: this.$store.getters['app/getMenuName']('simulation'),
      bodyAttrs: { class: 'simulation' }
    }
  },

  filters: {
    priceFormat,
  },

  data() {
    return {
      simulationBridalItems: [],
      editable: true,
      history: false,
      all: false,
      loading: false,
      itemModalTargetId: 0,
      amountDetail: {
        amount: 0,
        serviceCharge: 0,
        normalTaxRate: 0,
        reducedTaxRate: 0,
        amountWithoutTax: 0,
        serviceChargePrice: 0,
        normalAmountOfTax: 0,
        reducedAmountOfTax: 0,
      },
      displayAmountDetail: false,
    }
  },

  computed: {
    businessId() {
      return this.$store.getters['auth/businessId']
    },
    isPlanner() {
      return this.$store.getters['auth/isPlanner']
    },
    isCustomer() {
      return this.$store.getters['auth/isCustomer']
    },
    isUsageLimitOver() {
      return this.$store.getters['auth/isCustomer']
        && this.$store.getters['app/getUsageLimitFlag']
        && this.$store.getters['app/getUsageLimitDays'] === 0
        && !this.history
    },
    idParam() {
      return this.$route.params.id
    },
    groupTotalAmount: function() {
      return function(value) {
        const targetItems = this.simulationBridalItems.filter(
          item => item.bridal_item_group_id === value
          && !item.deleted
        )
        return targetItems.reduce((sum, item) => sum + item.retail_amount, 0)
      }
    },
    totalAmount() {
      return this.amountDetail.amount
    },
    // グループ・サブグループ・アイテムの3階層に整形して返す
    // （グループ名、サブグループ名の見出しを表示するのに使用）
    simulationBridalItemList() {
      let result = []
      for (const row of this.simulationBridalItems) {
        const groupId = row.bridal_item_group_id
        const subgroupId = row.bridal_item_subgroup_id
        if (result.find((row) => row.groupId === groupId) === undefined) {
          result.push({
            groupId: groupId,
            groupName: row.bridal_item_group_name,
            subgroupList: [],
          })
        }
        const groupIndex = result.length - 1
        if (
          result[groupIndex].subgroupList.find(
            (row) => row.subgroupId === subgroupId
          ) === undefined
        ) {
          result[groupIndex].subgroupList.push({
            subgroupId: subgroupId,
            subgroupName: row.bridal_item_subgroup_name,
            itemList: [],
          })
        }
        const subgroupIndex = result[groupIndex].subgroupList.length - 1
        result[groupIndex].subgroupList[subgroupIndex].itemList.push(row)
      }

      return result
    },
    simulationData() {
      let result = {
        "amount": this.amountDetail.amount,
        "amount_without_tax": this.amountDetail.amountWithoutTax,
        "service_charge": this.amountDetail.serviceCharge,
        "service_charge_price": this.amountDetail.serviceChargePrice,
        "normal_taxrate": this.amountDetail.normalTaxRate,
        "normal_amountof_tax": this.amountDetail.normalAmountOfTax,
        "reduced_taxrate": this.amountDetail.reducedTaxRate,
        "reduced_amountof_tax": this.amountDetail.reducedAmountOfTax,
        "simulation_bridal_item_list": []
      }

      for (const item of this.simulationBridalItems) {
        let simulationBridalItem = {
          simulation_bridal_item_id: item.simulation_bridal_item_id,
          retail_price: item.retail_price,
          quantity: item.quantity,
          fix_flag: item.fix_flag,
          retail_amount: item.retail_amount,
          target: !item.deleted,
          revision: item.revision,
        }
        if (!this.isPlanner) {
          // プランナー以外のとき確定フラグは不要なので削除
          delete simulationBridalItem.fix_flag
        }
        result.simulation_bridal_item_list.push(simulationBridalItem)
      }
      return result
    },
    //シミュレーション婚礼アイテム一覧から指定したIDのアイテム1件を取り出し、
    // BridalItemCard.vueに渡せる形に整形
    item() {
      return function(id) {
        let result = {
          bridal_item_id: 0,
          bridal_item_name: '',
          image_url: '',
          bridal_item_description: '',
          retail_price: 0,
          alert_message: null,
          favorited: false,
          simulation_target: false,
          collage_target: false,
          revision: 0,
          loading: false,
        }
        const selectedItem = this.simulationBridalItems.find(
          row => row.bridal_item_id === id
        )
        if (!selectedItem) {
          return result
        }
        result.bridal_item_id = selectedItem.bridal_item_id
        result.bridal_item_name = selectedItem.bridal_item_name
        result.image_url = selectedItem.image_path
        result.bridal_item_description = selectedItem.bridal_item_description
        result.retail_price = selectedItem.master_retail_price
        result.alert_message = selectedItem.alert_message

        return result
      }
    },

    // 税サ抜小計
    amountWithoutTax() {
      if (this.history) {
        return this.amountDetail.amountWithoutTax
      }
      return this.simulationBridalItems.reduce((sum, item) => {
        return !item.deleted ? sum + item.retail_amount : sum
      }, 0)
    },
    // サービス料合計金額
    serviceChargePrice() {
      if (this.history) {
        return this.amountDetail.serviceChargePrice
      }
      return Math.floor(this.simulationBridalItems.reduce((sum, item) => {
        return (!item.deleted && item.service_amount_target) ? sum + item.retail_amount : sum
      }, 0) * this.amountDetail.serviceCharge / 100)
    },
    // 標準税率合計金額
    normalAmountOfTax() {
      if (this.history) {
        return this.amountDetail.normalAmountOfTax
      }
      return Math.floor((this.simulationBridalItems.reduce((sum, item) => {
        return (!item.deleted && item.tax_exempt == "標準") ? sum + item.retail_amount : sum
      }, 0) + this.amountDetail.serviceChargePrice) * this.amountDetail.normalTaxRate / 100)
    },
    // 軽減税率合計金額
    reducedAmountOfTax() {
      if (this.history) {
        return this.amountDetail.reducedAmountOfTax
      }
      return Math.floor(this.simulationBridalItems.reduce((sum, item) => {
        return (!item.deleted && item.tax_exempt == "軽減") ? sum + item.retail_amount : sum
      }, 0) * this.amountDetail.reducedTaxRate / 100)
    },
  },

  methods: {
    // 画面初期化
    async fetchSimulationList() {
      this.loading = true
      this.editable = false
      this.history = false
      const hasIdParam = typeof this.idParam !== 'undefined'

      try {
        let simulation
        if (hasIdParam) {
          this.history = true
          // 履歴取得
          simulation = await simulationApi.getHistory(this.businessId, this.idParam)
        } else {
          // 最新取得
          if (!this.isUsageLimitOver) {
            // 最新かつ有効期限切れではないとき保存可
            this.editable = true
          }
          simulation = await simulationApi.getLatest(this.businessId)
        }
        // アイテムが1つでもあれば、全確定デフォルトはオン
        let isAllFix = (simulation.simulation_bridal_item_list.length > 0)
        for (const value of simulation.simulation_bridal_item_list) {
          // 削除フラグのデフォルトfalseを追加
          value.deleted = false
          if (!value.fix_flag) {
            // アイテムの確定が1つでもオフなら、全確定はオフ
            isAllFix = false
          }
        }
        this.all = isAllFix // 全確定チェックボックスをセット
        this.simulationBridalItems = simulation.simulation_bridal_item_list

        // (見積履歴 且つ 税サ抜金額が保存されている) 又は 最新取得 の場合は履歴の情報を表示する
        this.displayAmountDetail = ((this.history && simulation.amount_without_tax > 0) || (!this.history))

        // サービス料・税情報の初期化
        this.amountDetail.amount = simulation.amount
        this.amountDetail.amountWithoutTax = simulation.amount_without_tax
        this.amountDetail.serviceCharge = simulation.service_charge
        this.amountDetail.serviceChargePrice = simulation.service_charge_price
        this.amountDetail.normalTaxRate = simulation.normal_taxrate
        this.amountDetail.normalAmountOfTax = simulation.normal_amountof_tax
        this.amountDetail.reducedTaxRate = simulation.reduced_taxrate
        this.amountDetail.reducedAmountOfTax = simulation.reduced_amountof_tax
        this.calcAmountDetail()
      } catch(err) {
        renderError(err)
      } finally {
        this.loading = false
      }
    },

    // 金額変更
    onChangeRetailPrice(simulationBridalItemId, retailPrice) {
      const bridalItem = this.selectedSimulationBridalItem(simulationBridalItemId)
      // データはカンマ除去して保存
      bridalItem.retail_price = getCommaRemovedPrice(retailPrice)
      // 0を足すことで、-0の場合に0にする
      bridalItem.retail_amount = bridalItem.retail_price * bridalItem.quantity + 0
      // 再計算
      this.calcAmountDetail()
    },

    // 数量変更
    onChangeQuantity(simulationBridalItemId, quantity) {
      const bridalItem = this.selectedSimulationBridalItem(simulationBridalItemId)
      // データはカンマ除去して保存
      bridalItem.quantity = getCommaRemovedPrice(quantity)
      // 0を足すことで、-0の場合0にする
      bridalItem.retail_amount = bridalItem.retail_price * bridalItem.quantity + 0
      // 再計算
      this.calcAmountDetail()
    },

    // 変更した婚礼アイテムの抽出
    selectedSimulationBridalItem(simulationBridalItemId) {
      const bridalItem = this.simulationBridalItems.find(
        ({ simulation_bridal_item_id }) => simulation_bridal_item_id === simulationBridalItemId
      )
      return bridalItem
    },

    // 削除
    onDelete(simulationBridalItemId) {
      const bridalItem = this.selectedSimulationBridalItem(simulationBridalItemId)
      bridalItem.deleted = true
      // 再計算
      this.calcAmountDetail()
    },

    // 削除を戻す
    onUndo(simulationBridalItemId) {
      const bridalItem = this.selectedSimulationBridalItem(simulationBridalItemId)
      bridalItem.deleted = false
      // 再計算
      this.calcAmountDetail()
    },

    // チェックボックスの全て確定
    onAllCheckToggle() {
      const targets = this.simulationBridalItems
        .filter(item => !item.deleted)
        .map(item => item.simulation_bridal_item_id)
      for (const target of targets) {
        let bridalItem = this.selectedSimulationBridalItem(target)
        bridalItem.fix_flag = this.all
      }
    },

    // チェック操作
    onCheckToggle(simulationBridalItemId) {
      const bridalItem = this.selectedSimulationBridalItem(simulationBridalItemId)
      bridalItem.fix_flag = !bridalItem.fix_flag
      this.allCheck()
    },

    // 全てチェックをONにするかどうか判定
    allCheck() {
      const fixedItemLength = this.simulationBridalItems.filter(
        item => item.fix_flag
        && !item.deleted
      ).length
      const normalItemLength = this.simulationBridalItems.filter(
        item => !item.deleted
      ).length
      this.all = fixedItemLength === normalItemLength
    },

    // 保存
    async onSubmit() {
      if (this.loading) {
        return false
      }

      if (!this.validate()) {
        window.scrollTo(0, 0)
        return false
      }

      this.loading = true

      try {
        await simulationApi.update(
          this.businessId,
          this.simulationData
        )

        // Modal Open
        this.$modal.show('modal-complete')
        this.loading = false
      } catch (err) {
        renderError(err)
      }
    },

    modalOk() {
      this.$modal.close('modal-complete')
      window.scrollTo(0, 0)
      this.$withLoading(this.fetchSimulationList)
    },

    onShowItemModal(id) {
      this.itemModalTargetId = id
      this.$refs.bridalItemModal.show()
    },

    // 再計算
    calcAmountDetail() {
      this.amountDetail.amountWithoutTax = this.amountWithoutTax
      this.amountDetail.serviceChargePrice = this.serviceChargePrice
      this.amountDetail.normalAmountOfTax = this.normalAmountOfTax
      this.amountDetail.reducedAmountOfTax = this.reducedAmountOfTax

      if (!this.history) {
        this.amountDetail.amount =
          this.amountDetail.amountWithoutTax  +
          this.amountDetail.serviceChargePrice  +
          this.amountDetail.normalAmountOfTax  +
          this.amountDetail.reducedAmountOfTax
      }
    },
  },

  watch: {
    $route: {
      handler() {
        this.$withLoading(this.fetchSimulationList)
      }
    },
  },

  created() {
    this.$withLoading(this.fetchSimulationList)
  },
}
</script>
